import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import api, { type PaginatedResponse } from '../lib/api'
import { formatCurrency } from '../lib/format'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { FileUploadZone } from '../components/lead/FileUploadZone'
import { CardTitle as SectionCardTitle, PageHeader, SurfaceCard } from '../components/ui'

interface ChargebackMerchant {
  id: string
  dba_name: string
  fiserv_mid: string | null
  status: string
}

interface EvidenceFile {
  key: string
  file_name: string
  mime_type: string | null
  file_size: number | null
  uploaded_at: string | null
  download_url: string | null
}

interface Chargeback {
  id: string
  fiserv_case_id: string | null
  case_type: string | null
  amount: string
  reason_code: string | null
  card_type: string | null
  response_due_at: string | null
  days_to_due?: number | null
  status: string
  outcome: string | null
  case_fee: string
  response_submitted_at: string | null
  evidence_files?: EvidenceFile[]
  merchant?: ChargebackMerchant | null
}

function statusClass(status: string): string {
  switch (status) {
    case 'open':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'responded':
      return 'border-blue-200 bg-blue-50 text-blue-700'
    case 'won':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'lost':
      return 'border-gray-300 bg-gray-100 text-gray-700'
    case 'expired':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    default:
      return 'border-gray-200 bg-gray-50 text-gray-600'
  }
}

function deadlineLabel(chargeback: Pick<Chargeback, 'response_due_at' | 'days_to_due'>): string {
  if (typeof chargeback.days_to_due === 'number') {
    if (chargeback.days_to_due < 0) return `${Math.abs(chargeback.days_to_due)}d overdue`
    if (chargeback.days_to_due === 0) return 'Due today'
    if (chargeback.days_to_due === 1) return 'Due tomorrow'
    return `${chargeback.days_to_due}d left`
  }

  if (!chargeback.response_due_at) return 'No due date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(`${chargeback.response_due_at}T00:00:00`)
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86400000)
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  return `${diffDays}d left`
}

function StatusPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${statusClass(status)}`}>
      {status}
    </span>
  )
}

export function ChargebacksPage() {
  const { id } = useParams()
  return id ? <ChargebackDetail id={id} /> : <ChargebackList />
}

function ChargebackList() {
  const [status, setStatus] = useState('')
  const [reasonCode, setReasonCode] = useState('')
  const params = useMemo(() => ({
    status: status || undefined,
    reason_code: reasonCode.trim() || undefined,
    per_page: 50,
  }), [status, reasonCode])

  const { data, isLoading } = useQuery({
    queryKey: ['chargebacks', params],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Chargeback>>('/chargebacks', { params })
      return response.data
    },
  })

  return (
    <div className="space-y-6">
      <PageHeader title="Chargebacks" subtitle="Cross-merchant case queue for finance and management review." />

      <SurfaceCard padding="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm font-medium text-gray-700">
              Status
              <select
                className="mt-1 block rounded-xl border border-slate-200 px-3 py-2 text-sm"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="responded">Responded</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="expired">Expired</option>
              </select>
            </label>
            <Input
              label="Reason code"
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
              placeholder="10.4"
              className="w-32"
            />
            <Button variant="ghost" onClick={() => { setStatus(''); setReasonCode('') }}>Clear</Button>
          </div>
      </SurfaceCard>

      <SurfaceCard>
        <SectionCardTitle>Cases</SectionCardTitle>
        <div className="mt-4">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading chargebacks...</p>
          ) : data && data.data.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Case</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Merchant</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Reason</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Deadline</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.data.map((chargeback) => (
                    <tr key={chargeback.id} className="hover:bg-blue-50/60">
                      <td className="px-3 py-2">
                        <Link to={`/chargebacks/${chargeback.id}`} className="font-mono text-xs font-semibold text-blue-700 hover:text-blue-900">
                          {chargeback.fiserv_case_id ?? chargeback.id.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{chargeback.merchant?.dba_name ?? 'Unknown merchant'}</div>
                        <div className="text-xs text-gray-500">MID {chargeback.merchant?.fiserv_mid ?? '-'}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{chargeback.reason_code ?? '-'}</td>
                      <td className="px-3 py-2 text-right font-medium text-rose-700">{formatCurrency(Number(chargeback.amount))}</td>
                      <td className="px-3 py-2">
                        <div className="text-gray-700">{chargeback.response_due_at ?? '-'}</div>
                        <div className="text-xs text-gray-500">{deadlineLabel(chargeback)}</div>
                      </td>
                      <td className="px-3 py-2"><StatusPill status={chargeback.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">No chargebacks match the current filters.</p>
          )}
        </div>
      </SurfaceCard>
    </div>
  )
}

function ChargebackDetail({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const { canRespondChargebacks, canRecordChargebackOutcome } = usePermissions()
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<'won' | 'lost'>('won')
  const [caseFee, setCaseFee] = useState('')

  const { data: chargeback, isLoading } = useQuery({
    queryKey: ['chargeback', id],
    queryFn: async () => {
      const response = await api.get<Chargeback>(`/chargebacks/${id}`)
      return response.data
    },
  })

  const refresh = (updated: Chargeback) => {
    queryClient.setQueryData(['chargeback', id], updated)
    queryClient.invalidateQueries({ queryKey: ['chargebacks'] })
    queryClient.invalidateQueries({ queryKey: ['merchant-chargebacks'] })
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('files[]', file)
      const response = await api.post<Chargeback>(`/chargebacks/${id}/evidence`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onSuccess: (updated) => {
      setUploadError(null)
      refresh(updated)
    },
    onError: (error) => {
      setUploadError(apiErrorMessage(error, 'Upload failed.'))
    },
  })

  const respondMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<Chargeback>(`/chargebacks/${id}/respond`)
      return response.data
    },
    onSuccess: refresh,
  })

  const outcomeMutation = useMutation({
    mutationFn: async () => {
      const payload: { outcome: string; case_fee?: number } = { outcome }
      if (caseFee.trim()) payload.case_fee = Number(caseFee)
      const response = await api.post<Chargeback>(`/chargebacks/${id}/outcome`, payload)
      return response.data
    },
    onSuccess: (updated) => {
      refresh(updated)
      setCaseFee('')
    },
  })

  if (isLoading || !chargeback) {
    return <p className="py-8 text-center text-sm text-gray-500">Loading chargeback...</p>
  }

  const evidence = chargeback.evidence_files ?? []
  const canSubmit = canRespondChargebacks() && evidence.length > 0 && chargeback.status === 'open'

  return (
    <div className="space-y-6">
      <div>
        <Link to="/chargebacks" className="text-sm text-blue-700 hover:text-blue-900">Back to chargebacks</Link>
        <PageHeader
          className="mt-2"
          title={chargeback.fiserv_case_id ?? chargeback.id}
          subtitle={`${chargeback.merchant?.dba_name ?? 'Unknown merchant'} · MID ${chargeback.merchant?.fiserv_mid ?? '-'}`}
          action={<StatusPill status={chargeback.status} />}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          ['Amount', formatCurrency(Number(chargeback.amount))],
          ['Reason', chargeback.reason_code ?? '-'],
          ['Card', chargeback.card_type ?? '-'],
          ['Deadline', `${chargeback.response_due_at ?? '-'} (${deadlineLabel(chargeback)})`],
        ].map(([label, value]) => (
          <SurfaceCard key={label} padding="p-4">
              <p className="text-xs font-medium text-gray-500">{label}</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
          </SurfaceCard>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle>Evidence</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {evidence.length > 0 ? (
            <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {evidence.map((file) => (
                <div key={file.key} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-900">{file.file_name}</p>
                    <p className="text-xs text-gray-500">{file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Uploaded evidence'}</p>
                  </div>
                  {file.download_url ? (
                    <a className="text-blue-700 hover:text-blue-900" href={file.download_url} target="_blank" rel="noreferrer">Preview</a>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">No evidence uploaded yet.</p>
          )}

          {canRespondChargebacks() && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <FileUploadZone
                accept="application/pdf,image/jpeg,image/png,image/heic,text/plain,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                maxSizeMb={25}
                compact
                isUploading={uploadMutation.isPending}
                onFileSelect={(file) => uploadMutation.mutate(file)}
              />
              {uploadError && <p className="mt-2 text-sm text-red-600">{uploadError}</p>}
            </div>
          )}

          {canRespondChargebacks() && (
            <Button
              onClick={() => respondMutation.mutate()}
              disabled={!canSubmit}
              isLoading={respondMutation.isPending}
            >
              Submit response
            </Button>
          )}
        </CardContent>
      </Card>

      {canRecordChargebackOutcome() && (
        <Card>
          <CardHeader><CardTitle>Record Outcome</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm font-medium text-gray-700">
                Outcome
                <select
                  className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={outcome}
                  onChange={(event) => setOutcome(event.target.value as 'won' | 'lost')}
                >
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </label>
              <Input
                label="Case fee override"
                value={caseFee}
                onChange={(event) => setCaseFee(event.target.value)}
                placeholder={chargeback.case_fee}
                className="w-40"
              />
              <Button onClick={() => outcomeMutation.mutate()} isLoading={outcomeMutation.isPending}>
                Record outcome
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
